import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { forwardRef, useEffect, useState, useRef } from 'react';
import type { Group, Object3D, Skeleton, Bone } from 'three';
import { SkinnedMesh } from 'three';
import { hasValidSkeleton } from './utils/modelValidation';
import { getBoneByBodyPart, type BodyPart } from './utils/boneMapping';
import {
  useAnimationUpdate,
  type AnimationUpdateResult,
  type AnimationState,
} from './hooks/useAnimationUpdate';
import type { MotorPositions } from '../../stores/puppetStore';

/**
 * Props for the PuppetModel component
 */
interface PuppetModelProps {
  /**
   * Path to the GLTF/GLB model file
   * @default "/models/default-puppet.glb"
   * @note The default puppet model asset will be added in Task 12.5.
   *       Until then, provide a custom modelPath or the component will
   *       display an error state.
   */
  modelPath?: string;

  /**
   * Callback when model loading fails
   */
  onError?: (error: Error) => void;

  /**
   * Callback when model loads successfully
   */
  onLoad?: () => void;

  /**
   * Callback to receive animation state updates for debug panel
   * Called each frame when animation is active
   */
  onAnimationStateUpdate?: (state: AnimationState) => void;
}

/**
 * Ref interface exposing skeleton and bones for animation control
 */
export interface PuppetModelRef {
  /**
   * The skeleton object containing all bones
   */
  skeleton: Skeleton | null;

  /**
   * The root group of the loaded model
   */
  group: Group | null;

  /**
   * Get a bone by name for direct manipulation
   */
  getBoneByName: (name: string) => Bone | undefined;

  /**
   * Get a bone by body part using fuzzy matching
   * Handles different naming conventions (Mixamo, Character Creator, etc.)
   */
  getBoneByBodyPart: (bodyPart: BodyPart) => Bone | undefined;

  /**
   * Apply positions instantly to all bones (no animation)
   * Used for Reset to Start and Dock Puppet operations
   */
  applyPositions: (positions: MotorPositions) => boolean;

  /**
   * Set the animation state to the given positions
   * Used for Position Control Panel to sync with animation state
   */
  setAnimationState: (positions: MotorPositions) => void;
}

/**
 * PuppetModel Component
 *
 * Loads and renders a GLTF/GLB humanoid puppet model with skeleton for animation.
 * Provides error handling for model loading failures and exposes skeleton/bones
 * via ref for animation control.
 *
 * Features:
 * - GLTF/GLB loading with useGLTF hook from @react-three/drei
 * - Default humanoid model path: "/models/default-puppet.glb"
 * - Error handling for model loading failures
 * - Initializes to starting position (motor positions from puppet config)
 * - Exposes skeleton/bones via ref for animation
 * - Fuzzy bone matching by body part
 * - Real-time animation updates via useFrame
 * - Position application for Reset/Dock operations
 * - Animation state callback for debug panel integration
 *
 * @example
 * ```tsx
 * const puppetRef = useRef<PuppetModelRef>(null);
 *
 * // Access bones by name for animation
 * const headBone = puppetRef.current?.getBoneByName('Head');
 * if (headBone) {
 *   headBone.rotation.x = Math.PI / 4; // Nod head
 * }
 *
 * // Access bones by body part (handles different naming conventions)
 * const leftArm = puppetRef.current?.getBoneByBodyPart('leftArm');
 * if (leftArm) {
 *   leftArm.rotation.z = Math.PI / 4; // Raise left arm
 * }
 *
 * // Apply positions instantly (for Reset/Dock)
 * puppetRef.current?.applyPositions(startingPosition);
 *
 * <PuppetModel
 *   ref={puppetRef}
 *   modelPath="/models/custom-puppet.glb"
 *   onError={(error) => console.error('Failed to load model:', error)}
 *   onLoad={() => console.log('Model loaded successfully')}
 *   onAnimationStateUpdate={(state) => console.log('Animation state:', state)}
 * />
 * ```
 */
export const PuppetModel = forwardRef<PuppetModelRef, PuppetModelProps>(
  (
    {
      modelPath = '/models/default-puppet.glb',
      onError,
      onLoad,
      onAnimationStateUpdate,
    },
    ref
  ) => {
    const [error, setError] = useState<Error | null>(null);
    const [skeleton, setSkeleton] = useState<Skeleton | null>(null);
    const [group, setGroup] = useState<Group | null>(null);

    // Internal ref for animation updates
    const internalRef = useRef<PuppetModelRef>({
      skeleton: null,
      group: null,
      getBoneByName: () => undefined,
      getBoneByBodyPart: () => undefined,
      applyPositions: () => false,
    });

    // Load the GLTF model - useGLTF must be called unconditionally
    const gltf = useGLTF(modelPath);

    // Apply animation updates via useFrame and get control functions
    const animationControls: AnimationUpdateResult =
      useAnimationUpdate(internalRef);

    // Send animation state updates to parent via callback (Task 3.4)
    // Uses useFrame to get state each frame and call the callback
    useFrame(() => {
      if (onAnimationStateUpdate) {
        const state = animationControls.getAnimationState();
        onAnimationStateUpdate(state);
      }
    });

    // Validate and extract skeleton
    useEffect(() => {
      if (!gltf) return;

      let cancelled = false;

      // Helper function to find skeleton
      const findSkeleton = (object: Object3D): Skeleton | null => {
        if (object instanceof SkinnedMesh && object.skeleton) {
          return object.skeleton;
        }

        for (const child of object.children) {
          const found = findSkeleton(child);
          if (found) return found;
        }

        return null;
      };

      try {
        // Validate model has required skeleton
        if (!hasValidSkeleton(gltf)) {
          const validationError = new Error(
            'Invalid model: No skeleton found. Please load a model with a rigged skeleton.'
          );
          if (!cancelled) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Safe: cancelled flag prevents setState after unmount
            setError(validationError);
            onError?.(validationError);
          }
          return;
        }

        const foundSkeleton = findSkeleton(gltf.scene);
        if (foundSkeleton) {
          if (!cancelled) {
            setSkeleton(foundSkeleton);

            setGroup(gltf.scene as Group);
            onLoad?.();
          }
        } else {
          const skeletonError = new Error(
            'Failed to extract skeleton from model'
          );
          if (!cancelled) {
            setError(skeletonError);
            onError?.(skeletonError);
          }
        }
      } catch (err) {
        const processingError =
          err instanceof Error
            ? err
            : new Error('Error processing model skeleton');
        if (!cancelled) {
          setError(processingError);
          onError?.(processingError);
        }
      }

      return () => {
        cancelled = true;
      };
    }, [gltf, onError, onLoad]);

    // Expose skeleton and bones via ref
    useEffect(() => {
      const refValue: PuppetModelRef = {
        skeleton,
        group,
        getBoneByName: (name: string) => {
          if (!skeleton) return undefined;
          return skeleton.bones.find(
            (bone) => bone.name.toLowerCase() === name.toLowerCase()
          );
        },
        getBoneByBodyPart: (bodyPart: BodyPart) => {
          return getBoneByBodyPart(skeleton, bodyPart);
        },
        applyPositions: animationControls.applyPositions,
        setAnimationState: animationControls.resetAnimationState,
      };

      // Update internal ref for animation hook
      internalRef.current = refValue;

      // Update external ref
      if (typeof ref === 'function') {
        ref(refValue);
      } else if (ref) {
        ref.current = refValue;
      }
    }, [
      skeleton,
      group,
      ref,
      animationControls.applyPositions,
      animationControls.resetAnimationState,
    ]);

    // Display error state
    if (error) {
      return (
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[1, 2, 0.5]} />
          <meshStandardMaterial color="#ef4444" wireframe />
        </mesh>
      );
    }

    // Render the model (starting position applied via useAnimationUpdate)
    if (gltf?.scene) {
      return <primitive object={gltf.scene} />;
    }

    return null;
  }
);

PuppetModel.displayName = 'PuppetModel';
